import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import { MdArrowBack, MdNavigation, MdRefresh, MdRoute, MdMyLocation, MdZoomOutMap, MdClose, MdPlaylistAddCheck, MdHelp, MdSwapHoriz } from "react-icons/md"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"
import { configAnim } from "../../../servicos/config/configAnim"
import { Spinner } from "../../../componentes/Spinner"
import { copy } from "../../../servicos/Uteis"
import { marcaIntegracao } from "../../../servicos/marcaIntegracao"
import {
	LIMITE_ENDERECOS_OSRM,
	MODOS_ROTA,
	enderecosMaisProximos,
	filtrarEnderecosParaRota,
	listarServicosRota,
	resumirParadas,
	formatarDuracao,
	formatarKm,
	formatarValor,
	headingNormalizado,
	headingContinuo,
	escolherHeading,
	recortarPolylineFrente,
	deslocouOSuficiente,
	saiuDaRota,
	juntarTrechoComResto,
	etaAteProximo,
	HEADING_MORTO_ROTA,
	HEADING_PASSO_ROTA,
	HEADING_MORTO_PARADO,
	HEADING_PASSO_PARADO,
	BUSSOLA_SILENCIO_MS,
	trocarParadas,
} from "./rotaServicosGeo"
import { montarRotaTableRoute, tracarRotaNaOrdem } from "./osrmTableRoute"
import { calcularRotaAteProximo } from "./osrmRouteMapp"
import { abrirGpsNativo, obterPosicaoRotaServicos, iniciarBussola, pedirPermissaoBussola } from "./rotaServicosLocalizacao"
import { dominioTemMinhaRota, lerMinhaRotaVisivel } from "../../../utils/preferenciaMinhaRota"
import RotaServicosMapa from "./RotaServicosMapa"
import "./RotaServicos.css"

const PILL_BTN = {
	padding: 0,
	width: 40,
	height: 40,
	minWidth: 40,
	minHeight: 40,
	margin: 0,
	border: 0,
	borderRadius: "50%",
	background: "var(--primaria, #100940)",
	color: "#fff",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
	opacity: 1,
	overflow: "visible",
	lineHeight: 1,
}

const SHEET_MIN = 320
const SHEET_MAX_VH = 0.62
const STORAGE_MODO = "rs-modo-rota"
const STORAGE_INTRO = "rs-intro-visto"

const OPCOES_MODO = [
	{ id: MODOS_ROTA.COLETAS, label: "Coletas", dots: ["coleta"], dica: "Só as coletas em aberto" },
	{ id: MODOS_ROTA.LIBERADAS, label: "Liberadas", dots: ["coleta", "entrega"], dica: "Coletas e entregas já coletadas" },
	{ id: MODOS_ROTA.TODAS, label: "Todas", dots: ["coleta", "entrega"], dica: "Todos os pontos, mesmo sem coletar" },
]

function lerModoSalvo() {
	try {
		const v = localStorage.getItem(STORAGE_MODO)
		if (v === MODOS_ROTA.COLETAS || v === MODOS_ROTA.LIBERADAS || v === MODOS_ROTA.TODAS) return v
	} catch { /* ignore */ }
	return MODOS_ROTA.LIBERADAS
}

function salvarModo(modo) {
	try { localStorage.setItem(STORAGE_MODO, modo) } catch { /* ignore */ }
}

function lerIntroVisto() {
	try { return localStorage.getItem(STORAGE_INTRO) === "1" } catch { return false }
}

function salvarIntroVisto() {
	try { localStorage.setItem(STORAGE_INTRO, "1") } catch { /* ignore */ }
}

function IntroRota({ onFechar }) {
	return (
		<div className="rs-intro" onClick={onFechar} role="dialog" aria-modal="true" aria-labelledby="rs-intro-titulo">
			<motion.div
				className="rs-intro-card"
				initial={{ opacity: 0, y: 22, scale: 0.96 }}
				animate={{ opacity: 1, y: 0, scale: 1 }}
				transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="rs-intro-pins" aria-hidden="true">
					<span className="rs-legenda-pin is-coleta">C</span>
					<span className="rs-legenda-pin is-entrega">E</span>
				</div>
				<h2 id="rs-intro-titulo">Minha rota</h2>
				<p className="rs-intro-lead">
					Traça o caminho dos seus pontos, na ordem, e acompanha você como um GPS.
				</p>
				<ul className="rs-intro-lista">
					{OPCOES_MODO.map((op) => (
						<li key={op.id}>
							<span className="rs-seg-dots">
								{op.dots.map((d) => (
									<i key={d} className={`rs-dot rs-dot-${d}`} />
								))}
							</span>
							<strong>{op.label}</strong>
							<span>{op.dica}</span>
						</li>
					))}
				</ul>
				<button type="button" className="rs-intro-btn" onClick={onFechar}>
					Entendi
				</button>
			</motion.div>
		</div>
	)
}

function ChipTipo({ tipoPonto }) {
	const coleta = tipoPonto === "coleta"
	return (
		<span className={`rs-chip ${coleta ? "rs-chip-coleta" : "rs-chip-entrega"}`}>
			{coleta ? "Coleta" : "Entrega"}
		</span>
	)
}

function Chip({ children, className = "", style }) {
	return <span className={`rs-chip ${className}`} style={style}>{children}</span>
}

function AcoesPonto({ ponto, onIrServico }) {
	if (!ponto) return null
	return (
		<div className="rs-acoes">
			<button type="button" className="rs-btn rs-btn-pri" onClick={() => onIrServico(ponto)}>
				Ir ao serviço
			</button>
			<button type="button" className="rs-btn rs-btn-sec" onClick={() => abrirGpsNativo(ponto.la, ponto.lo)}>
				<MdNavigation size={18} />
				GPS
			</button>
		</div>
	)
}

function DetalhePonto({ ponto, total, onIrServico, pontos, escolhendoTroca, onAbrirTroca, onCancelarTroca, onTrocar }) {
	if (!ponto) return null
	const marca = ponto.ifood ? marcaIntegracao({ "s.tipo": ponto.tipo, "s.ifood": ponto.ifood }) : null
	const valor = formatarValor(ponto.valor)
	const dist = ponto.distanciaServico && ponto.distanciaServico !== "-"
		? String(ponto.distanciaServico).replace(".", ",") + " km"
		: ""

	return (
		<div>
			<div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
				<Chip className="rs-chip-prox">{ponto.parada} / {total}</Chip>
				<ChipTipo tipoPonto={ponto.tipoPonto} />
				{ponto.urgente ? <Chip className="rs-chip-urg">Urgente</Chip> : null}
				{marca ? (
					<Chip style={{ background: marca.fundo, color: marca.texto }}>{marca.nome}</Chip>
				) : null}
			</div>
			<div className="rs-endereco">{ponto.rua || "Endereço sem descrição"}</div>
			{ponto.complemento ? <div className="rs-meta">{ponto.complemento}</div> : null}

			<div className="rs-detalhe-bloco">
				<small>Serviço</small>
				<div className="rs-meta" style={{ fontWeight: 700, color: "#12141a" }}>
					#{ponto.idServico}
					{ponto.status ? ` · ${ponto.status}` : ""}
				</div>
				{ponto.nomeCliente ? <div className="rs-meta">{ponto.nomeCliente}</div> : null}
				{(valor || dist) ? (
					<div className="rs-meta">{[valor, dist].filter(Boolean).join(" · ")}</div>
				) : null}
			</div>

			{(ponto.contato || ponto.telefone || ponto.observacao || ponto.codigoIfood) ? (
				<div className="rs-detalhe-bloco">
					<small>Contato</small>
					{ponto.contato ? <div className="rs-meta">{ponto.contato}</div> : null}
					{ponto.telefone ? (
						<button type="button" className="rs-tel" onClick={() => copy(ponto.telefone)}>
							{ponto.telefone}
						</button>
					) : null}
					{ponto.observacao ? <div className="rs-meta">{ponto.observacao}</div> : null}
					{ponto.codigoIfood ? <div className="rs-meta">Localizador {ponto.codigoIfood}</div> : null}
				</div>
			) : null}

			<AcoesPonto ponto={ponto} onIrServico={onIrServico} />
			{onTrocar ? (
				<TrocaOrdem
					ponto={ponto}
					pontos={pontos}
					aberto={escolhendoTroca}
					onAbrir={onAbrirTroca}
					onCancelar={onCancelarTroca}
					onEscolher={onTrocar}
				/>
			) : null}
		</div>
	)
}

function TrocaOrdem({ ponto, pontos, aberto, onAbrir, onCancelar, onEscolher }) {
	const outros = (pontos || []).filter((p) => p.key !== ponto?.key)
	if (!ponto || outros.length < 1) return null

	if (!aberto) {
		return (
			<button type="button" className="rs-btn rs-btn-sec rs-btn-troca" onClick={onAbrir}>
				<MdSwapHoriz size={20} />
				Trocar ordem
			</button>
		)
	}

	return (
		<div className="rs-troca">
			<div className="rs-troca-label">Trocar o ponto {ponto.parada} com</div>
			<div className="rs-troca-ops">
				{outros.map((p) => (
					<button
						key={p.key}
						type="button"
						className={`rs-troca-op ${p.tipoPonto === "coleta" ? "is-coleta" : "is-entrega"}`}
						onClick={() => onEscolher(p)}
						aria-label={`Trocar com o ponto ${p.parada}`}
					>
						{p.parada}
					</button>
				))}
			</div>
			<button type="button" className="rs-btn rs-btn-sec" onClick={onCancelar}>
				Cancelar
			</button>
		</div>
	)
}

export default function RotaServicos() {
	const navegar = useNavigate()
	const { dados } = useContext(ContextoGeral)

	const [carregando, setCarregando] = useState(true)
	const [calculando, setCalculando] = useState(false)
	const [erroRota, setErroRota] = useState("")
	const [posProf, setPosProf] = useState(null)
	const [heading, setHeading] = useState(0)
	const [rotacao, setRotacao] = useState(0)
	const [pontos, setPontos] = useState([])
	const [polylineCompleta, setPolylineCompleta] = useState([])
	const [metricas, setMetricas] = useState({})
	const [foraDaRota, setForaDaRota] = useState(0)
	const [seguir, setSeguir] = useState(true)
	const [focoProf, setFocoProf] = useState(0)
	const [sheetAlto, setSheetAlto] = useState(false)
	const [selecionado, setSelecionado] = useState(null)
	const [tickVista, setTickVista] = useState(0)
	const [vazioMsg, setVazioMsg] = useState("")
	const [recalculando, setRecalculando] = useState(false)
	const [linhaCongelada, setLinhaCongelada] = useState(null)
	const [servicos, setServicos] = useState([])
	const [modo, setModo] = useState(lerModoSalvo)
	const [idsEscolhidos, setIdsEscolhidos] = useState([])
	const [sheetOs, setSheetOs] = useState(false)
	const [buscaOs, setBuscaOs] = useState("")
	const [idsDraft, setIdsDraft] = useState([])
	const [introAberto, setIntroAberto] = useState(() => !lerIntroVisto())
	const [dicaFiltro, setDicaFiltro] = useState(null)
	const [escolhendoTroca, setEscolhendoTroca] = useState(false)
	const [trocandoRota, setTrocandoRota] = useState(false)

	const posRef = useRef(null)
	const proximoRef = useRef(null)
	const mapRef = useRef(null)
	const dadosRef = useRef(dados)
	const polylineRef = useRef([])
	const ticksForaRef = useRef(0)
	const recalcandoRef = useRef(false)
	const ultimoRecalcRef = useRef(0)
	const bussolaRef = useRef(null)
	const moveuEmRef = useRef(0)
	const dragY = useRef(null)
	const handleArrastou = useRef(false)
	const servicosRef = useRef([])
	const modoRef = useRef(modo)
	const idsRef = useRef(idsEscolhidos)
	const rotaAbortRef = useRef(null)
	const dicaTimerRef = useRef(null)
	const introAbertoRef = useRef(introAberto)
	introAbertoRef.current = introAberto

	dadosRef.current = dados
	servicosRef.current = servicos
	modoRef.current = modo
	idsRef.current = idsEscolhidos
	const proximo = pontos.find((p) => Number(p.parada) === 1) || null
	proximoRef.current = proximo
	polylineRef.current = polylineCompleta
	const listaOs = useMemo(() => listarServicosRota(servicos), [servicos])
	const listaOsFiltrada = useMemo(() => {
		const q = buscaOs.trim().toLowerCase()
		if (!q) return listaOs
		return listaOs.filter((os) =>
			String(os.id).includes(q) || String(os.nome).toLowerCase().includes(q)
		)
	}, [listaOs, buscaOs])

	const polyline = useMemo(
		() => linhaCongelada || recortarPolylineFrente(posProf, polylineCompleta),
		[posProf, polylineCompleta, linhaCongelada]
	)
	const sheetPx = !selecionado && !sheetOs
		? 24
		: (sheetOs || sheetAlto)
			? Math.round(window.innerHeight * SHEET_MAX_VH)
			: SHEET_MIN

	const resumoTopo = useMemo(() => resumirParadas(pontos), [pontos])

	const tracarRota = useCallback(async (listaServicos, pos, modoAtual, ids) => {
		rotaAbortRef.current?.abort()
		const ctrl = new AbortController()
		rotaAbortRef.current = ctrl

		const abertos = filtrarEnderecosParaRota(listaServicos, modoAtual, ids)
		const recorte = enderecosMaisProximos(pos, abertos, LIMITE_ENDERECOS_OSRM)
		setForaDaRota(Math.max(0, abertos.length - recorte.length))
		setErroRota("")

		if (!recorte.length) {
			setPontos([])
			setPolylineCompleta([])
			setMetricas({})
			setVazioMsg(listaServicos.length ? "filtro" : "vazio")
			setCalculando(false)
			return
		}

		setPontos(recorte.map((e) => ({ ...e, parada: "" })))
		setVazioMsg("")
		setCalculando(true)

		try {
			const rota = await montarRotaTableRoute([{ la: pos.la, lo: pos.lo, key: "voce" }, ...recorte], ctrl.signal)
			if (ctrl.signal.aborted) return
			if (rota.pontos.length) setPontos(rota.pontos)
			setPolylineCompleta(rota.polyline || [])
			setMetricas(rota.metricas || {})
			if (!rota.ok) {
				setErroRota("Não foi possível traçar a rota. Os pontos estão na ordem de proximidade na via.")
			}
		} catch (err) {
			if (ctrl.signal.aborted || err?.name === "AbortError") return
			console.warn("[Minha rota] OSRM:", err)
			setPolylineCompleta([])
			setMetricas({})
			setErroRota("Rota indisponível no momento. Os pontos estão na ordem de proximidade.")
		} finally {
			if (!ctrl.signal.aborted) {
				setCalculando(false)
				setTickVista((n) => n + 1)
				setSeguir(true)
			}
		}
	}, [])

	const etaProx = useMemo(
		() => etaAteProximo(posProf, polylineCompleta, proximo, metricas),
		[posProf, polylineCompleta, proximo, metricas],
	)

	const carregar = useCallback(async () => {
		if (!lerMinhaRotaVisivel(dados?.idProf)) {
			setCarregando(false)
			return
		}
		setCarregando(true)
		setErroRota("")
		setSelecionado(null)
		setSheetAlto(false)
		setSheetOs(false)
		setEscolhendoTroca(false)
		setVazioMsg("")
		ticksForaRef.current = 0

		const pos = await obterPosicaoRotaServicos()
		if (!pos) {
			setPosProf(null)
			setPontos([])
			setPolylineCompleta([])
			setServicos([])
			setVazioMsg("gps")
			setCarregando(false)
			return
		}
		setPosProf(pos)
		posRef.current = pos

		const retorno = await ServicoAPI.buscarServicos(dados)
		if (!retorno?.resultado || !Array.isArray(retorno.dados)) {
			setPontos([])
			setPolylineCompleta([])
			setServicos([])
			setVazioMsg("lista")
			setCarregando(false)
			return
		}

		const lista = retorno.dados
		setServicos(lista)
		servicosRef.current = lista
		setCarregando(false)
		await tracarRota(lista, pos, modoRef.current, idsRef.current)
	}, [dados, tracarRota])

	function fecharIntro() {
		salvarIntroVisto()
		setIntroAberto(false)
	}

	function abrirIntro() {
		if (dicaTimerRef.current) clearTimeout(dicaTimerRef.current)
		setDicaFiltro(null)
		setIntroAberto(true)
	}

	function mostrarDicaFiltro(idModo) {
		if (introAbertoRef.current) return
		const op = OPCOES_MODO.find((o) => o.id === idModo)
		if (!op) return
		if (dicaTimerRef.current) clearTimeout(dicaTimerRef.current)
		setDicaFiltro({ ...op, tick: Date.now() })
		dicaTimerRef.current = setTimeout(() => setDicaFiltro(null), 2400)
	}

	function aplicarModo(novo) {
		mostrarDicaFiltro(novo)
		setModo(novo)
		modoRef.current = novo
		salvarModo(novo)
		setSelecionado(null)
		setEscolhendoTroca(false)
		if (posRef.current && servicosRef.current.length) {
			tracarRota(servicosRef.current, posRef.current, novo, idsRef.current)
		}
	}

	function abrirSeletorOs() {
		setIdsDraft(idsEscolhidos)
		setBuscaOs("")
		setSelecionado(null)
		setSheetOs(true)
	}

	function aplicarOs() {
		setIdsEscolhidos(idsDraft)
		idsRef.current = idsDraft
		setSheetOs(false)
		setSelecionado(null)
		if (posRef.current && servicosRef.current.length) {
			tracarRota(servicosRef.current, posRef.current, modoRef.current, idsDraft)
		}
	}

	function toggleOs(id) {
		const chave = String(id)
		setIdsDraft((atual) => (
			atual.map(String).includes(chave)
				? atual.filter((x) => String(x) !== chave)
				: [...atual, id]
		))
	}

	useEffect(() => {
		if (!lerMinhaRotaVisivel(dados?.idProf)) return
		carregar()
		return () => {
			rotaAbortRef.current?.abort()
			if (dicaTimerRef.current) clearTimeout(dicaTimerRef.current)
		}
	}, [carregar, dados?.idProf])

	useEffect(() => {
		if (!lerMinhaRotaVisivel(dados?.idProf)) return
		return iniciarBussola((h) => {
			bussolaRef.current = h
			if (Date.now() - moveuEmRef.current < BUSSOLA_SILENCIO_MS) return
			setHeading(headingNormalizado(h))
			setRotacao((atual) => headingContinuo(atual, h, HEADING_MORTO_PARADO, HEADING_PASSO_PARADO))
		})
	}, [dados?.idProf])

	useEffect(() => {
		if (!lerMinhaRotaVisivel(dados?.idProf)) return
		const pedir = () => { pedirPermissaoBussola() }
		window.addEventListener("pointerdown", pedir, { capture: true, once: true })
		window.addEventListener("touchstart", pedir, { capture: true, once: true, passive: true })
		window.addEventListener("click", pedir, { capture: true, once: true })
		return () => {
			window.removeEventListener("pointerdown", pedir, { capture: true })
			window.removeEventListener("touchstart", pedir, { capture: true })
			window.removeEventListener("click", pedir, { capture: true })
		}
	}, [dados?.idProf])

	useEffect(() => {
		if (!lerMinhaRotaVisivel(dados?.idProf)) return
		let ativo = true
		const aplicarPos = (pos) => {
			if (!ativo || !pos) return
			const anterior = posRef.current
			if (anterior && deslocouOSuficiente(anterior, pos, 0.012)) moveuEmRef.current = Date.now()
			const andando = Date.now() - moveuEmRef.current < BUSSOLA_SILENCIO_MS
			const rumo = escolherHeading(pos, anterior, proximoRef.current, {
				bussola: bussolaRef.current,
				line: polylineRef.current,
				usarBussola: !andando,
			})
			if (rumo != null) {
				setHeading(headingNormalizado(rumo))
				setRotacao((atual) => headingContinuo(
					atual,
					rumo,
					andando ? HEADING_MORTO_ROTA : HEADING_MORTO_PARADO,
					andando ? HEADING_PASSO_ROTA : HEADING_PASSO_PARADO,
				))
			}
			posRef.current = pos
			setPosProf(pos)

			if (recalcandoRef.current) return
			const fora = saiuDaRota(pos, polylineRef.current, rumo ?? bussolaRef.current)
			ticksForaRef.current = fora ? ticksForaRef.current + 1 : 0

			const prox = proximoRef.current
			const agora = Date.now()
			if (
				ticksForaRef.current >= 4
				&& prox
				&& agora - ultimoRecalcRef.current > 8000
			) {
				recalcandoRef.current = true
				ultimoRecalcRef.current = agora
				ticksForaRef.current = 0
				setLinhaCongelada(recortarPolylineFrente(pos, polylineRef.current))
				setRecalculando(true)
				const lineAntiga = polylineRef.current
				calcularRotaAteProximo(dadosRef.current, pos, prox)
					.then((line) => {
						if (!ativo || !line.length) return
						setPolylineCompleta(juntarTrechoComResto(line, lineAntiga, prox))
					})
					.catch((err) => console.warn("[Minha rota] recálculo:", err))
					.finally(() => {
						recalcandoRef.current = false
						setRecalculando(false)
						setLinhaCongelada(null)
					})
			}
		}
		const id = setInterval(async () => {
			aplicarPos(await obterPosicaoRotaServicos())
		}, 1500)
		aplicarPos(posRef.current)
		return () => {
			ativo = false
			clearInterval(id)
		}
	}, [dados?.idProf])

	function irAoServico(ponto) {
		if (!ponto?.idServico) return
		navegar(`/app/servicos/${ponto.idServico}`, {
			state: { idServico: ponto.idServico },
		})
	}

	async function confirmarTroca(alvo) {
		const atual = selecionado
		if (!atual?.key || !alvo?.key || atual.key === alvo.key) return
		const novos = trocarParadas(pontos, atual.key, alvo.key)
		setEscolhendoTroca(false)
		setPontos(novos)
		setSelecionado(novos.find((p) => p.key === atual.key) || novos[0])

		const pos = posRef.current
		if (!pos) return

		rotaAbortRef.current?.abort()
		const ctrl = new AbortController()
		rotaAbortRef.current = ctrl
		setTrocandoRota(true)
		setCalculando(true)
		setErroRota("")
		setSeguir(false)

		try {
			const rota = await tracarRotaNaOrdem(
				[{ la: pos.la, lo: pos.lo, key: "voce" }, ...novos],
				ctrl.signal,
			)
			if (ctrl.signal.aborted) return
			if (rota.pontos.length) {
				setPontos(rota.pontos)
				const sel = rota.pontos.find((p) => p.key === atual.key)
				if (sel) setSelecionado(sel)
			}
			setPolylineCompleta(rota.polyline || [])
			setMetricas(rota.metricas || {})
			if (!rota.ok) setErroRota("Não foi possível traçar a rota nesta ordem.")
		} catch (err) {
			if (ctrl.signal.aborted || err?.name === "AbortError") return
			console.warn("[Minha rota] troca de ordem:", err)
			setErroRota("Rota indisponível no momento. A ordem dos pontos foi mantida.")
		} finally {
			if (!ctrl.signal.aborted) {
				setCalculando(false)
				setTrocandoRota(false)
				setTickVista((n) => n + 1)
			}
		}
	}

	const fecharSheet = useCallback(() => {
		setSelecionado(null)
		setSheetAlto(false)
		setEscolhendoTroca(false)
	}, [])

	const onMapa = useCallback((mapa) => {
		mapRef.current = mapa
	}, [])

	function onHandleDown(e) {
		handleArrastou.current = false
		dragY.current = e.clientY ?? e.touches?.[0]?.clientY
	}

	function onHandleUp(e) {
		const y = e.clientY ?? e.changedTouches?.[0]?.clientY
		if (dragY.current == null || y == null) return
		const dy = y - dragY.current
		dragY.current = null
		if (Math.abs(dy) > 28) handleArrastou.current = true
		if (dy < -28) setSheetAlto(true)
		else if (dy > 28) {
			if (sheetAlto) setSheetAlto(false)
			else {
				setSelecionado(null)
				setSheetAlto(false)
			}
		}
	}

	const seguirBottom = !selecionado && !sheetOs
		? "calc(24px + env(safe-area-inset-bottom, 0px))"
		: (sheetOs || sheetAlto)
			? `calc(${Math.round(SHEET_MAX_VH * 100)}vh + 12px)`
			: `${SHEET_MIN + 12}px`

	if (!dominioTemMinhaRota(dados?.dominio) || !lerMinhaRotaVisivel(dados?.idProf)) {
		return <Navigate to="/app/home" replace />
	}

	return (
		<motion.div
			className="rs-page"
			initial={configAnim.initial}
			animate={configAnim.animate}
			exit={configAnim.exit}
			transition={configAnim.transition}
			transformTemplate={({ x }) => {
				if (x === "0%" || x === 0 || x === "0px" || !x) return "none"
				return `translateX(${x})`
			}}
		>
			<div className="rs-mapa">
				<div
					className={`rs-mapa-giro${seguir && focoProf > 0 ? " is-gps" : ""}`}
					style={{ transform: `rotate(${seguir && focoProf > 0 ? -rotacao : 0}deg)` }}
				>
					<RotaServicosMapa
						pontos={pontos}
						polyline={polyline}
						posProf={posProf}
						heading={heading}
						seguir={seguir}
						rotacao={seguir && focoProf > 0 ? rotacao : 0}
						focoProf={focoProf}
						onPararSeguir={() => setSeguir(false)}
						onMapa={onMapa}
						onSelect={(p) => {
							setSheetOs(false)
							setSelecionado(p)
							setSheetAlto(false)
							setEscolhendoTroca(false)
						}}
						onFecharSheet={fecharSheet}
						tick={tickVista}
						sheetPx={sheetPx}
						iconeProf={dados?.iconeTipoVeiculo}
					/>
				</div>
			</div>

			<div className="rs-lado-esq">
				<div className="rs-zoom">
					<button type="button" aria-label="Aproximar" onClick={() => mapRef.current?.zoomIn()}>+</button>
					<button type="button" aria-label="Afastar" onClick={() => mapRef.current?.zoomOut()}>−</button>
				</div>
				{pontos.length > 0 && (
					<div className="rs-legenda" role="note" aria-label="Legenda: C coleta, E entrega">
						<span className="rs-legenda-item">
							<span className="rs-legenda-pin is-coleta" aria-hidden="true">C</span>
							Coleta
						</span>
						<span className="rs-legenda-item">
							<span className="rs-legenda-pin is-entrega" aria-hidden="true">E</span>
							Entrega
						</span>
					</div>
				)}
			</div>

			<div className="rs-topo">
				<div className="rs-pill">
					<button type="button" className="rs-pill-btn" style={PILL_BTN} onClick={() => navegar("/app/home")} aria-label="Voltar">
						<MdArrowBack size={22} color="#fff" />
					</button>
					<div className="rs-pill-titulo">
						<strong>Minha rota</strong>
						<span>{resumoTopo || "Pontos em aberto"}</span>
					</div>
					<button
						type="button"
						className="rs-pill-btn"
						style={PILL_BTN}
						onClick={carregar}
						disabled={carregando || calculando}
						aria-label="Recalcular"
					>
						<MdRefresh size={22} color="#fff" />
					</button>
				</div>
				<div className="rs-filtros">
					<div className="rs-seg" role="tablist" aria-label="Filtro da rota">
						{OPCOES_MODO.map((op) => (
							<button
								key={op.id}
								type="button"
								role="tab"
								aria-selected={modo === op.id}
								className={`rs-seg-btn${modo === op.id ? " is-ativo" : ""}`}
								onClick={() => aplicarModo(op.id)}
							>
								<span className="rs-seg-dots">
									{op.dots.map((d) => (
										<i key={d} className={`rs-dot rs-dot-${d}`} />
									))}
								</span>
								{op.label}
							</button>
						))}
					</div>
					<button
						type="button"
						className={`rs-os-btn${idsEscolhidos.length ? " is-ativo" : ""}`}
						onClick={abrirSeletorOs}
						aria-label="Selecionar serviços"
						title="Selecionar serviços"
					>
						<MdPlaylistAddCheck size={22} />
						{idsEscolhidos.length > 0 && <span className="rs-os-badge">{idsEscolhidos.length}</span>}
					</button>
				</div>
				<AnimatePresence>
					{dicaFiltro && !introAberto && (
						<motion.div
							key={dicaFiltro.tick}
							className="rs-dica-wrap"
							initial={{ opacity: 0, y: -8 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -6 }}
							transition={{ duration: 0.2 }}
						>
							<div className="rs-dica" role="status">
								<span className="rs-seg-dots">
									{dicaFiltro.dots.map((d) => (
										<i key={d} className={`rs-dot rs-dot-${d}`} />
									))}
								</span>
								<span>{dicaFiltro.dica}</span>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{posProf && pontos.length > 0 && etaProx && (
				<div className="rs-eta" style={{ bottom: seguirBottom }}>
					<div className="rs-eta-tempo">
						{etaProx.km < 0.05 ? "Chegando" : (formatarDuracao(etaProx.seg) || "< 1 min")}
					</div>
					<div className="rs-eta-lado">
						<strong>{etaProx.km < 0.05 ? formatarKm(etaProx.km) || "agora" : formatarKm(etaProx.km)}</strong>
						<span>até o próximo{proximo?.rua ? ` · ${proximo.rua}` : ""}</span>
					</div>
				</div>
			)}

			<div className="rs-fabs" style={{ bottom: seguirBottom }}>
				<button
					type="button"
					className="rs-seguir"
					onClick={abrirIntro}
					aria-label="Como funciona a Minha rota"
					title="Como funciona"
				>
					<MdHelp size={24} />
				</button>
				{posProf && pontos.length > 0 && (
					<>
						<button
							type="button"
							className="rs-seguir"
							onClick={() => {
								setSeguir(false)
								setTickVista((n) => n + 1)
							}}
							aria-label="Ver rota toda"
							title="Ver rota toda"
						>
							<MdZoomOutMap size={22} />
						</button>
						<button
							type="button"
							className={`rs-seguir${seguir ? " is-ativo" : ""}`}
							onClick={() => {
								pedirPermissaoBussola()
								setSeguir(true)
								setFocoProf((n) => n + 1)
							}}
							aria-label="Minha posição"
							title="Minha posição"
						>
							<MdMyLocation size={22} />
						</button>
					</>
				)}
			</div>

			{erroRota && !selecionado && pontos.length > 0 && (
				<div className="rs-alerta rs-alerta-flutuante">{erroRota}</div>
			)}

			{recalculando && (
				<div className="rs-recalc">Recalculando rota…</div>
			)}

			{(carregando || calculando) && (
				<div className="rs-overlay">
					<Spinner />
					<span>
						{carregando
							? "Buscando endereços…"
							: trocandoRota
								? "Traçando a ordem escolhida…"
								: modo === MODOS_ROTA.COLETAS
									? "Traçando coletas…"
									: modo === MODOS_ROTA.LIBERADAS
										? "Traçando coletas e entregas liberadas…"
										: "Traçando a melhor ordem…"}
					</span>
				</div>
			)}

			{!carregando && !pontos.length && (
				<div className="rs-vazio">
					<MdRoute size={36} color="var(--primaria, #100940)" />
					<h2>
						{vazioMsg === "gps"
							? "Ative a localização"
							: vazioMsg === "filtro"
								? (modo === MODOS_ROTA.COLETAS ? "Nenhuma coleta em aberto" : "Nada neste filtro")
								: "Nada em aberto"}
					</h2>
					<p>
						{vazioMsg === "gps"
							? "Precisamos da sua posição para escolher os pontos mais próximos."
							: vazioMsg === "lista"
								? "Não foi possível carregar os serviços em execução."
								: vazioMsg === "filtro" && modo === MODOS_ROTA.COLETAS
									? "Todas as coletas já foram feitas. Veja as entregas liberadas."
									: vazioMsg === "filtro"
										? "Nenhum ponto bate com o filtro ou com os serviços escolhidos."
										: "Não há endereços abertos com coordenada para roteirizar."}
					</p>
					{vazioMsg === "filtro" && modo === MODOS_ROTA.COLETAS ? (
						<button type="button" className="rs-btn rs-btn-pri" style={{ width: "100%" }} onClick={() => aplicarModo(MODOS_ROTA.LIBERADAS)}>
							Ver liberadas
						</button>
					) : (
						<button type="button" className="rs-btn rs-btn-pri" style={{ width: "100%" }} onClick={() => navegar("/app/servicos")}>
							Ir para serviços
						</button>
					)}
				</div>
			)}

			{!carregando && pontos.length > 0 && selecionado && (
				<div
					className="rs-sheet"
					style={{ height: sheetAlto ? `${Math.round(SHEET_MAX_VH * 100)}vh` : "auto", minHeight: SHEET_MIN }}
				>
					<div
						className="rs-handle"
						onPointerDown={onHandleDown}
						onPointerUp={onHandleUp}
						onClick={() => {
							if (handleArrastou.current) return
							if (sheetAlto) setSheetAlto(false)
							else fecharSheet()
						}}
					>
						<div className="rs-handle-bar" />
						<span>{sheetAlto ? "toque para recolher" : "puxe para ver a fila"}</span>
					</div>

					{sheetAlto ? (
						<div className="rs-fila">
							{foraDaRota > 0 && (
								<div className="rs-alerta">+{foraDaRota} ponto(s) fora desta rota (mais distantes)</div>
							)}
							{erroRota ? <div className="rs-alerta">{erroRota}</div> : null}
							{pontos.map((p) => (
								<button
									key={p.key}
									type="button"
									className={`rs-fila-item ${p.key === selecionado?.key ? "is-atual" : ""}`}
									onClick={() => {
										setSelecionado(p)
										setSheetAlto(false)
										setEscolhendoTroca(false)
									}}
								>
									<div className={`rs-num ${p.tipoPonto === "coleta" ? "is-coleta" : "is-entrega"}${p.key === proximo?.key ? " is-prox" : ""}`}>
										{p.tipoPonto === "coleta" ? "C" : "E"}
									</div>
									<div style={{ minWidth: 0 }}>
										<div style={{ fontWeight: 700, fontSize: "0.92rem" }} className="truncate">{p.rua}</div>
										<div className="rs-meta">#{p.idServico}{p.nomeCliente ? ` · ${p.nomeCliente}` : ""} · {p.parada}ª</div>
									</div>
								</button>
							))}
						</div>
					) : (
						<DetalhePonto
							ponto={selecionado}
							total={pontos.length}
							onIrServico={irAoServico}
							pontos={pontos}
							escolhendoTroca={escolhendoTroca}
							onAbrirTroca={() => setEscolhendoTroca(true)}
							onCancelarTroca={() => setEscolhendoTroca(false)}
							onTrocar={confirmarTroca}
						/>
					)}
				</div>
			)}

			{sheetOs && (
				<div className="rs-sheet rs-sheet-os" style={{ height: `${Math.round(SHEET_MAX_VH * 100)}vh` }}>
					<div className="rs-os-topo">
						<div>
							<strong>Escolher serviços</strong>
							<span>{listaOs.length} em execução</span>
						</div>
						<button type="button" className="rs-os-fechar" onClick={() => setSheetOs(false)} aria-label="Fechar">
							<MdClose size={22} />
						</button>
					</div>
					<input
						className="rs-os-busca"
						type="search"
						placeholder="Buscar por número ou cliente"
						value={buscaOs}
						onChange={(e) => setBuscaOs(e.target.value)}
					/>
					<div className="rs-os-lista">
						{listaOsFiltrada.map((os) => {
							const todos = idsDraft.length === 0
							const marcado = todos || idsDraft.map(String).includes(String(os.id))
							return (
								<button
									key={os.id}
									type="button"
									className={`rs-os-item${marcado ? " is-on" : ""}`}
									onClick={() => {
										if (todos) setIdsDraft([os.id])
										else toggleOs(os.id)
									}}
								>
									<span className={`rs-check${marcado ? " is-on" : ""}`} />
									<div style={{ minWidth: 0, flex: 1 }}>
										<div className="rs-os-nome truncate">#{os.id}{os.nome ? ` · ${os.nome}` : ""}</div>
										<div className="rs-meta">{os.status}</div>
									</div>
									<div className="rs-os-qtds">
										{os.qtdColeta > 0 && <span className="rs-chip rs-chip-coleta">{os.qtdColeta}C</span>}
										{os.qtdEntrega > 0 && <span className="rs-chip rs-chip-entrega">{os.qtdEntrega}E</span>}
									</div>
								</button>
							)
						})}
						{!listaOsFiltrada.length && <p className="rs-meta" style={{ padding: "12px 4px" }}>Nenhum serviço encontrado.</p>}
					</div>
					<div className="rs-os-rodape">
						<button type="button" className="rs-btn rs-btn-sec" onClick={() => setIdsDraft([])}>
							Todas as OS
						</button>
						<button type="button" className="rs-btn rs-btn-pri" onClick={aplicarOs}>
							{idsDraft.length ? `Roteirizar ${idsDraft.length}` : "Roteirizar todas"}
						</button>
					</div>
				</div>
			)}
			{introAberto && <IntroRota onFechar={fecharIntro} />}
		</motion.div>
	)
}
